import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  BodySmallMuted,
  DetailsPageDescription,
  DetailsPageHeader,
  NoSearchResults,
  Spinner,
} from '@common/components';
import { ContentParser } from '@common/components/ContentParser';
import SectionContainer from '@common/components/SectionContainer';
import { Routes } from '@common/constants';
import { RoomRateConfigurationWizard } from '@features/RoomRateConfigurationWizard';
import { analytics } from '@features/analytics';
import { OnChangeParams } from '@features/JourneyPicker/JourneyPicker.types';
import { UpgradeItemVersion } from '@features/List/BookableList/UpgradeItem';
import { NotFoundWrapper } from '@features/List/ListPage.styles';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import { useRate } from '@features/rates';
import { formatCheckInOutLine } from '@features/rates/helpers/formatCheckInOutLine.helper';
import { moreRoomsAvailableThanInBasket } from '@features/rates/helpers/moreRoomsAvailableThanInBasket.helper';
import { useDenialAnalytics } from '@features/rates/helpers/useDenialAnalytics.helper';
import { useRoomSettings } from '@features/roomSettings';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';
import rewriteHttp from '@utils/rewriteHttp';
import moment, { Moment } from 'moment';
import { FC } from 'react';
import { useRouter } from 'shared-routing';
import { JourneyParams, MultiRoomSearch } from 'shared-types/Common';
import { RateErrorMessage, RateRoomType, RoomUpgrade } from 'shared-types/Rate';
import styled from 'styled-components';
import { DatesPicker } from './DatesPicker';
import { RoomsListing } from './RoomsListing';

type RateDetailProps = {
  journeyParams: JourneyParams;
  changeJourneyParam: (
    name: string,
    value?: string | Moment | number | MultiRoomSearch | Record<string, number>
  ) => void;
  setJourneyParams: (params: OnChangeParams) => void;
  language: string;
  errorReason: RateErrorMessage;
  roomIdsInBasket: string[];
  backRoute?: string;
  backText?: string;
};

const RateDetailWrapper = styled(SectionContainer)`
  margin-top: 10px;
`;

export const RateDetail: FC<RateDetailProps> = ({
  journeyParams,
  changeJourneyParam,
  setJourneyParams,
  language,
  errorReason,
  roomIdsInBasket,
  backRoute,
  backText,
}) => {
  const errorMessage = useRatesErrorMessage(errorReason);
  const { query } = useRouter();
  const rateCode = query.id as string;
  const promoRateCode = typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined;
  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const childAges = journeyParams.childAges;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);

  const { rate, isFetching, isLoading } = useRate(
    rateCode,
    {
      fromDate,
      toDate,
      adults,
      children,
      childAges,
      promoRateCode,
      rooms,
    },
    language
  );

  const { data } = useRoomSettings();
  const roomSettings = data?.data;

  const { room, bookingStep } = query;
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const { t } = useTranslations();
  const pageDescriptionTitle = t('rate.seeRateDetails');
  const noSearchHeading = t('rate.noResults');
  const noSearchButtonText = backText ?? t('app.backToRates');
  const selectedRoom = rate?.rooms?.find((thisRoom) => thisRoom.id === room);
  const upgrade = selectedRoom?.roomUpgrade;
  const linkParams = { ...query, bookingStep: 'guestDetailsStep' };
  const upgradeLinkParams = { ...linkParams, room: upgrade?.id };

  const checkInOutLine = formatCheckInOutLine(t, roomSettings);

  const availableRooms = (rate?.rooms || []).filter((room) =>
    moreRoomsAvailableThanInBasket(room, roomIdsInBasket)
  );
  const allUpgradeRoomsInBasket =
    !!upgrade &&
    (upgrade?.quantity || Infinity) < roomIdsInBasket?.filter((id) => id === upgrade.id)?.length;

  useDenialAnalytics({
    isFetching,
    hasAvailability: availableRooms.length > 0,
    itemCategory: 'rates',
    itemId: rate?.id,
    itemName: rate?.title ?? '',
    fromDate: journeyParams.fromDate,
    toDate: journeyParams.toDate,
  });

  if (isLoading || isFetching) {
    return <Spinner showDelayMessage isActive />;
  }

  const isUpgradeStep = bookingStep === 'upgrade' && !!upgrade;

  const upgradeItem = (
    <UpgradeItemVersion
      isOpen={isUpgradeStep}
      {...(upgrade as RoomUpgrade)}
      linkRoute={Routes.RATE_BOOKING}
      linkParams={linkParams}
      upgradeLinkParams={upgradeLinkParams}
      language={language}
      numberOfNights={moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days')}
      originalRoom={
        selectedRoom
          ? { id: selectedRoom.id, name: selectedRoom.name, imageUrl: selectedRoom.imageUrl }
          : undefined
      }
    />
  );

  if (rate && isEcom2 && isUpgradeStep) {
    return upgradeItem;
  }

  return rate ? (
    <RateDetailWrapper>
      <DetailsPageHeader
        name={rate.title}
        image={{
          url: rewriteHttp(rate.imageUrl),
        }}
        metadata={{
          price: rate?.price || null,
        }}
      />
      <DetailsPageDescription title={pageDescriptionTitle}>
        <ContentParser text={rate.description} escapeHtml={false} />

        {checkInOutLine && <p>{checkInOutLine}</p>}

        {rate.cancellationPolicyText && (
          <ContentParser text={rate.cancellationPolicyText} escapeHtml={false} />
        )}
      </DetailsPageDescription>
      <DatesPicker
        journeyParams={journeyParams}
        rateCode={rate.id}
        showGuestsPicker={true}
        onChangeJourneyParams={(params: OnChangeParams) => {
          const { fromDate, toDate, adults, childAges, children, rooms } = params;

          changeJourneyParam('fromDate', fromDate);
          changeJourneyParam('toDate', toDate);
          changeJourneyParam(
            'adults',
            (params.rooms || []).reduce((acc, room) => acc + room.adults, 0) ?? adults
          );
          changeJourneyParam(
            'children',
            (params.rooms || []).reduce((acc, room) => acc + room.children, 0) ?? children
          );
          changeJourneyParam('children', children);
          changeJourneyParam('rooms', rooms);
          changeJourneyParam('childAges', childAges);
          setJourneyParams(params);
        }}
        trackGaUpdate={({ fromDate, toDate, adults, children }: OnChangeParams) => {
          analytics.track({
            event: 'view_item',
            params: {
              adults,
              children,
              start_date: moment(fromDate).format('YYYY-MM-DD').toString(),
              end_date: moment(toDate).format('YYYY-MM-DD').toString(),
              items: [{ item_category: 'rates', item_id: rate.id, item_name: rate.title ?? '' }],
            },
          });
        }}
      />
      {errorReason ? (
        <NotFoundWrapper>
          {errorMessage.title}
          <br />
          <BodySmallMuted>{errorMessage.message}</BodySmallMuted>
        </NotFoundWrapper>
      ) : (
        <RoomsListing
          rate={{
            ...rate,
            name: rate.title,
            fromPrice: rate.price,
            rooms: availableRooms as RateRoomType[],
          }}
          journeyParams={journeyParams}
          isFetching={isFetching}
          allUpgradeRoomsInBasket={allUpgradeRoomsInBasket}
        />
      )}
      <RoomRateConfigurationWizard
        bookingStep={bookingStep as string}
        language={language}
        metaEntity={{ type: 'rate', ...rate } as any}
        entity={{ type: 'room', id: room } as any}
      />
      {upgradeItem}
    </RateDetailWrapper>
  ) : (
    <NoSearchResults
      headingText={noSearchHeading}
      linkRoute={backRoute ?? Routes.RATE_LIST}
      linkText={noSearchButtonText}
    />
  );
};
